import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-trans-jobs',
    templateUrl: './trans-jobs.component.html',
    styleUrls: ['./trans-jobs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TransJobsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  GENERAL_CONFIG: any = {};
  private headerData: any = {};
  private globalParams: any = {};
  private userSettings: any = {};
  filterParams: any = {};

  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';

  filteredTaxYearData: any[] = [];
  selectedTaxYear: string = '';
  scenarioDropDown: any[] = [];
  selectedScenario: string = '';
  showscenario = false;
  showTransTypes = false;
  savingGroupKey: string | null = null;

  trans_types: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.GENERAL_CONFIG = this.headerData.generalConfigData || {};
    this.globalParams = this.headerData.globalParams || {};
    this.userSettings = this.headerData.userSettingsData || {};
    const activeScreen =
      this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    this.curentyear = this.globalParams.tax_year || '';
    this.scenario = this.globalParams.scenario || '';
    this.jcd_key = this.globalParams.jcd_key || '';
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.checkLockStatus();
    this.getTaxYearDropDown();
    this.cdr.detectChanges();
  }

  private checkLockStatus(): void {
    const lockparams = {
      action_code: 'n10cpv',
      scenario: this.scenario,
    };
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        lockparams,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        const locked = data?.jsonObject;
        if (locked?.[0]?.LOCK_STATUS == 'Y') {
          this.gtwSnackbarService.openSnackBar(
            'Tax Return has already been filed for tax year ' +
              this.filterParams.tax_year +
              '.',
            'gtw-snackbar--red',
            true,
            4
          );
        }
        this.cdr.detectChanges();
      });
  }

  getTaxYearDropDown(): void {
    const params = { action_code: 'e83e1x' };
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        const taxYearData = _.filter(data?.jsonObject, (p: any) =>
          _.includes(['TAX_YEAR'], p.CODE_GROUP)
        );
        this.filteredTaxYearData = [];
        const currentYear = parseInt(this.curentyear, 10);
        for (const ty of taxYearData) {
          const desc = parseInt(ty.CODE_DISP_DESC, 10);
          if (
            ty.CODE_GROUP == 'TAX_YEAR' &&
            desc <= currentYear &&
            desc >= currentYear - 5
          ) {
            this.filteredTaxYearData.push(ty);
          }
        }
        this.filteredTaxYearData.sort(
          (a: any, b: any) =>
            parseInt(b.CODE_DISP_DESC, 10) - parseInt(a.CODE_DISP_DESC, 10)
        );
        this.cdr.detectChanges();
      });
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year to get the Scenario.',
        'gtw-snackbar--red',
        true,
        4
      );
      return;
    }
    const params = {
      action_code: '7q0wiy',
      tax_year: this.selectedTaxYear,
    };
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.showscenario = true;
        this.getTransType();
        this.cdr.detectChanges();
      });
  }

  onScenarioChange(): void {
    this.showTransTypes = true;
    this.cdr.detectChanges();
  }

  getTransType(): void {
    const params = {
      action_code: 'mgl3cv',
      p_scenario: this.scenario,
      p_jcd_key: this.jcd_key,
    };
    this.jsonObjectFactory
      .getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.trans_types = (data?.jsonObject || []).map((type: any) => ({
          ...type,
          IS_ENABLED: type.IS_ENABLED === 'Y',
        }));
        this.cdr.detectChanges();
      });
  }

  toggleTransJob(t: any): void {
    if (this.savingGroupKey !== null) return;
    t.IS_ENABLED = !t.IS_ENABLED;
    this.updateTransJob(t.GROUP_KEY, t.IS_ENABLED);
  }

  updateTransJob(groupKey: string, isEnabled: boolean): void {
    this.savingGroupKey = groupKey;
    this.cdr.detectChanges();

    const issueParams = this.gtwGlobal.isIssueIDEnabled(
      this.selectedTaxYear,
      this.selectedScenario
    );
    const params: any = {
      sso_id: this.userSettings?.user?.sso_id,
      action_code: 'g01dos',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      issue_id: this.globalParams.issue_id || '',
      issue_short_desc: this.globalParams.issue_short_desc || '',
      scenarioCode: issueParams.scenarioCode || '',
      is_issue_key_enabled: issueParams.is_issue_key_enabled || 'N',
      GROUP_KEY: groupKey,
      IS_ENABLED: isEnabled ? 'Y' : 'N',
    };

    this.jsonObjectFactory
      .saveJSON(
        this.GENERAL_CONFIG.base_url + '/saveJsonObject',
        params,
        this.headerData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        () => {
          this.savingGroupKey = null;
          this.gtwSnackbarService.openSnackBar(
            'Transaction type updated successfully.',
            'gtw-snackbar--green',
            true,
            3
          );
          this.getTransType();
        },
        () => {
          this.savingGroupKey = null;
          this.gtwSnackbarService.openSnackBar(
            'Error updating trans job. Please try again.',
            'gtw-snackbar--red',
            true,
            4
          );
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
